import React from 'react';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import { Button } from '@mui/material';

import { ParentPaper } from '../atoms/parent-paper';
import { SwitchBox } from '../molecules/switch-box';
import { Group } from '../organisms/group';
import { FormattedValue } from '../store/formatted-value';
import { InputBox } from '../molecules/input-box';
import { SliderBox } from '../molecules/slider-box';
import { useSelector } from '../store/utils';
import { SimpleTable } from '../molecules/simple-table';
import { StatsDelete } from '../organisms/stats-delete';
import { StatsExport } from '../organisms/stats-export';
import { AppMessagesDelete } from '../organisms/app-messages-delete';
import { Breadcrumbs } from '../atoms/breadcrumbs';

import { links } from '../paths';
import { AuditLogDelete } from '../organisms/audit-log-delete';
import { ReportTest } from '../organisms/report-test';
import { ValueBox } from '../molecules/value-box';
import { SelectBox } from '../molecules/select-box';
import {
  GridMeteringMode,
  HeatWater,
  Inverter,
  InverterPowerControl,
} from '../shared';
import { BoilerTestTrigger } from '../organisms/boiler-test-wizard/boiler-test-trigger';

const ExpertService = () => {
  const [serviceActive, serviceLoginList, errorList] = useSelector(
    ({ variables }) =>
      [
        variables.SERVICE_ACTIVE,
        variables.LOGIN_LIST,
        variables.ERR_LIST,
      ] as const
  );

  const hwProportional = useSelector((s) => {
    return s.variables.HW_CONTROL_TYPE === HeatWater.Proportional;
  });

  const goodwe = useSelector((s) => {
    return s.variables.PV_INVERTER_TYPE === Inverter.GoodWe;
  });

  if (!serviceActive) {
    return (
      <Group
        layout="list"
        title={
          <FormattedMessage
            id="expert_service.title"
            defaultMessage="Service"
          />
        }
      >
        <InputBox
          title={
            <FormattedMessage
              id="expertService.password"
              defaultMessage="Přístup do servisu"
            />
          }
          inputVariableId="SERVICE_PASSWORD"
          label={
            <FormattedMessage
              id="expertService.password.label"
              defaultMessage="Heslo"
            />
          }
          fullWidth
        />
      </Group>
    );
  }

  return (
    <>
      <Group
        title={
          <FormattedMessage
            id="expertService.general"
            defaultMessage="Obecné nastavení"
          />
        }
        layout="list"
      >
        <ParentPaper>
          <SwitchBox
            title={
              <FormattedMessage
                id="expertService.general.largeInstallation"
                defaultMessage="Velká instalace"
              />
            }
            subtitle={
              <FormattedMessage
                id="expertService.general.largeInstallation.subtitle"
                defaultMessage="Rozšíří rozsahy hodnot pro velké instalace"
              />
            }
            switchVariableId="LARGE_INSTALLATION"
            fullWidth
          />
        </ParentPaper>
      </Group>

      <Group
        title={
          <FormattedMessage
            id="expertService.PV"
            defaultMessage="Fotovoltaika"
          />
        }
        layout="list"
      >
        <ParentPaper>
          <InputBox
            title={
              <FormattedMessage
                id="expertService.PV.IpAdr"
                defaultMessage="IP adresa střídače"
              />
            }
            inputVariableId="MODBUS_TEST_ADDRESS"
            fullWidth
          />
          <SwitchBox
            title={
              <FormattedMessage
                id="expertService.PV.CntComOk"
                defaultMessage="Čítač validních zpráv"
              />
            }
            subtitle={<FormattedValue variableId="PV_cnt_com_ok" />}
            fullWidth
          />
          <SwitchBox
            title={
              <FormattedMessage
                id="expertService.PV.CntComErr"
                defaultMessage="Čítač chybných zpráv"
              />
            }
            subtitle={<FormattedValue variableId="PV_cnt_com_err" />}
            fullWidth
          />
          <ValueBox
            type="row"
            title={
              <FormattedMessage
                id="expertService.PV.Versions"
                defaultMessage="Verze"
              />
            }
            value={<FormattedValue variableId="PV_INVERTER_VERSIONS" />}
          />
          {goodwe && (
            <SliderBox
              variableId="PV_BATTERY_POWER_PERCENT"
              title={
                <FormattedMessage
                  id="expertService.PV.BatteryPowerPercent"
                  defaultMessage="Výkon nabíjení baterie v procentech"
                />
              }
              opts={{
                style: 'percent',
                maximumFractionDigits: 0,
              }}
              min={0}
              max={100}
            />
          )}
          <SliderBox
            title={
              <FormattedMessage
                id="expertService.PV.StringNumber"
                defaultMessage="Počet stingů střídače"
              />
            }
            subtitle={
              <FormattedMessage
                id="expertService.PV.StringNumber.subtitle"
                defaultMessage="Počet stingů střídače zobrazovaný v aplikaci na záložce FVE"
              />
            }
            variableId="PV_STRING_NUMBER"
            opts={{
              maximumFractionDigits: 0,
            }}
            min={1}
            max={6}
          />
          <SwitchBox
            title={
              <FormattedMessage
                id="expertService.PV.ExternalPv"
                defaultMessage="Externí FVE připojená skrze SOLAX Meter 2"
              />
            }
            switchVariableId="PV_SOLAX_EXTERNAL_PV"
            fullWidth
          />
          <SwitchBox
            title={
              <FormattedMessage
                id="expertService.PV.ExternalPvSofar"
                defaultMessage="Externí FVE připojená skrze SOFAR Meter 2"
              />
            }
            switchVariableId="PV_SOFAR_EXTERNAL_PV"
            fullWidth
          />
          <SelectBox
            title={
              <FormattedMessage
                id="expertService.PV.PowerControlType"
                defaultMessage="Typ řízení výkonu síťových střídačů"
              />
            }
            selectVariableId="PV_POWER_CONTROL_TYPE"
            options={[
              {
                label: (
                  <FormattedMessage
                    id="expertService.PV.PowerControlType.proportional"
                    defaultMessage="Plynulé"
                  />
                ),
                value: InverterPowerControl.Proportional,
              },
              {
                label: (
                  <FormattedMessage
                    id="expertService.PV.PowerControlType.fixed"
                    defaultMessage="Fixní"
                  />
                ),
                value: InverterPowerControl.Fixed,
              },
            ]}
            fullWidth
          />
          <SelectBox
            title={
              <FormattedMessage
                id="expertService.PV.MeteringMode"
                defaultMessage="Způsob měření distribuce"
              />
            }
            subtitle={
              <FormattedMessage
                id="expertService.PV.MeteringMode.subtitle"
                defaultMessage="Fázové měření (ČR) počítá nákup i přetok na každé fázi zvlášť. Součtové měření pracuje s výslednou bilancí všech fází."
              />
            }
            selectVariableId="EM_METERING_MODE"
            options={[
              {
                label: (
                  <FormattedMessage
                    id="expertService.PV.MeteringMode.perPhase"
                    defaultMessage="Fázové"
                  />
                ),
                value: GridMeteringMode.PerPhase,
              },
              {
                label: (
                  <FormattedMessage
                    id="expertService.PV.MeteringMode.sum"
                    defaultMessage="Součtové"
                  />
                ),
                value: GridMeteringMode.Sum,
              },
            ]}
            fullWidth
          />
          <SelectBox
            title={
              <FormattedMessage
                id="expertService.PV.ControlMeteringMode"
                defaultMessage="Způsob řízení přebytků"
              />
            }
            subtitle={
              <FormattedMessage
                id="expertService.PV.ControlMeteringMode.subtitle"
                defaultMessage="Fázové řízení počítá přebytek pro řízení technologií z jednotlivých fází. Součtové řízení pracuje se součtem všech fází."
              />
            }
            selectVariableId="PV_CONTROL_METERING_MODE"
            options={[
              {
                label: (
                  <FormattedMessage
                    id="expertService.PV.ControlMeteringMode.perPhase"
                    defaultMessage="Fázové"
                  />
                ),
                value: GridMeteringMode.PerPhase,
              },
              {
                label: (
                  <FormattedMessage
                    id="expertService.PV.ControlMeteringMode.sum"
                    defaultMessage="Součtové"
                  />
                ),
                value: GridMeteringMode.Sum,
              },
            ]}
            fullWidth
          />
        </ParentPaper>
      </Group>
      <Group
        title={
          <FormattedMessage id="expertService.HW" defaultMessage="Bojler" />
        }
        layout="list"
      >
        <ParentPaper>
          <SelectBox
            title={
              <FormattedMessage
                id="expertService.HW.control"
                defaultMessage="Typ řízení bojleru"
              />
            }
            selectVariableId="HW_CONTROL_TYPE"
            options={[
              {
                label: (
                  <FormattedMessage
                    id="expertService.HW.control.proportional"
                    defaultMessage="Plynulé"
                  />
                ),
                value: HeatWater.Proportional,
              },
              {
                label: (
                  <FormattedMessage
                    id="expertService.HW.control.onoff"
                    defaultMessage="On/Off"
                  />
                ),
                value: HeatWater.Digital,
              },
            ]}
            fullWidth
          />
          {hwProportional && (
            <SliderBox
              title={
                <FormattedMessage
                  id="expertService.HW.maxPower"
                  defaultMessage="Maximální výkon bojleru v procentech"
                />
              }
              subtitle={
                <FormattedMessage
                  id="expertService.HW.maxPower.subtitle"
                  defaultMessage="Toto nastavení je pouze pro plynulé řízení bojleru a omezuje maximální výkon patrony v procentech."
                />
              }
              variableId="HW_EK_POWER_PERC_LIMIT"
              opts={{ style: 'percent', maximumFractionDigits: 0 }}
              min={20}
              max={100}
            />
          )}
          <SliderBox
            title={
              <FormattedMessage
                id="expertService.HW.hysterezis"
                defaultMessage="Hystereze bojleru"
              />
            }
            subtitle={
              <FormattedMessage
                id="expertService.HW.hysterezis.subtitle"
                defaultMessage="Hystereze je pouze pro běžný ohřev"
              />
            }
            variableId="HW_HYST"
            opts={{
              style: 'unit',
              unit: 'celsius',
              maximumFractionDigits: 0,
            }}
            min={1}
            max={10}
          />
          <SliderBox
            title={
              <FormattedMessage
                id="expertService.HW.HwBatteryPriority"
                defaultMessage="Procento nabítí od kterého má vždy prioritu bojler"
              />
            }
            variableId="PV_OFFSET_SOC_HW_PRIO"
            opts={{ style: 'percent', maximumFractionDigits: 0 }}
            min={30}
            max={97}
          />
          <SliderBox
            title={
              <FormattedMessage
                id="expertService.HW.offset.disabled-overflow"
                defaultMessage="Offset pro řízení bojleru se zakázaným přetokem na fázi [W]"
              />
            }
            variableId="OFFSET_FOR_NOT_OVERFLOW"
            subtitle={
              <FormattedMessage
                id="expertService.HW.offset.disabled-overflow.subtitle"
                defaultMessage="Vyšší číslo umožní bojleru odebírat více energie ze sítě při omezeném střídači"
              />
            }
            opts={{
              maximumFractionDigits: 1,
              minimumFractionDigits: 1,
            }}
            min={-1000}
            max={9000}
            step={5}
          />
          <SliderBox
            title={
              <FormattedMessage
                id="expertService.HW.offset.enabled-overflow"
                defaultMessage="Offset pro řízení bojleru s povoleným přetokem na fázi [W]"
              />
            }
            variableId="OFFSET_FOR_OVERFLOW"
            subtitle={
              <FormattedMessage
                id="expertService.HW.offset.enabled-overflow.subtitle"
                defaultMessage="Vyšší číslo umožní bojleru odebírat více energie ze sítě."
              />
            }
            opts={{
              maximumFractionDigits: 1,
              minimumFractionDigits: 1,
            }}
            min={-1000}
            max={9000}
            step={5}
          />
          <SliderBox
            title={
              <FormattedMessage
                id="expertService.HW.offset.ongrid"
                defaultMessage="Offset pro řízení bojleru pro síťové elektrárny [W]"
              />
            }
            variableId="OFFSET_ON_GRID"
            subtitle={
              <FormattedMessage
                id="expertService.HW.offset.ongrid.subtitle"
                defaultMessage="Vyšší číslo umožní bojleru odebírat více energie ze sítě."
              />
            }
            opts={{
              maximumFractionDigits: 1,
              minimumFractionDigits: 1,
            }}
            min={-1000}
            max={9000}
            step={5}
          />
          <SwitchBox
            title={
              <FormattedMessage
                id="expertService.HW.offset"
                defaultMessage="Offset na fázi [W]"
              />
            }
            subtitle={<FormattedValue variableId="PV_EM_OFFSET_REAL" />}
            fullWidth
          />
          <SwitchBox
            title={
              <FormattedMessage
                id="expertService.HW.TempId"
                defaultMessage="ID teplotního čidla"
              />
            }
            subtitle={<FormattedValue variableId="TEMP_IDS[0]" />}
            fullWidth
          />
          <SwitchBox
            title={
              <FormattedMessage
                id="expertService.HW.HwErrEnabled"
                defaultMessage="Vyhodnocovat dostupnost bojleru"
              />
            }
            switchVariableId="HW_ERR_POWER_ENABLED"
            fullWidth
          />
          <SliderBox
            type="minutes"
            title={
              <FormattedMessage
                id="expertService.HW.HwResetErrorTime"
                defaultMessage="Prodleva pro reset chyby termostatu"
              />
            }
            subtitle={
              <FormattedMessage
                id="expertService.HW.HwResetErrorTime.subtitle"
                defaultMessage="Čas v minutách po kterém se chyba termostatu resetuje a opět se spustí ohřev vody"
              />
            }
            opts={{
              maximumFractionDigits: 0,
              minimumFractionDigits: 0,
            }}
            min={300}
            max={5400}
            step={300}
            variableId="HW_ERR_RESET_TIME"
          />
          <SwitchBox
            title={
              <FormattedMessage
                id="expertService.HW.LegionellaLastTime"
                defaultMessage="Poslední dosažení teploty legionelly"
              />
            }
            subtitle={<FormattedValue variableId="HW_LEGIONELLA_LAST_TIME" />}
            fullWidth
          />
          <BoilerTestTrigger />
        </ParentPaper>
      </Group>
      <Group
        title={
          <FormattedMessage
            id="expertService.HP"
            defaultMessage="Tepelné čerpadlo"
          />
        }
        layout="list"
      >
        <ParentPaper>
          <InputBox
            title={
              <FormattedMessage
                id="expertService.HP.IpAdr"
                defaultMessage="IP adresa tepelného čerpadla"
              />
            }
            inputVariableId="HP_ECOONE_API_IP"
            fullWidth
          />
          <SwitchBox
            title={
              <FormattedMessage
                id="expertService.HP.CntComOk"
                defaultMessage="Čítač validních zpráv"
              />
            }
            subtitle={<FormattedValue variableId="HP_cnt_com_ok" />}
            fullWidth
          />
          <SwitchBox
            title={
              <FormattedMessage
                id="expertService.HP.CntComErr"
                defaultMessage="Čítač chybných zpráv"
              />
            }
            subtitle={<FormattedValue variableId="HP_cnt_com_err" />}
            fullWidth
          />
          <SliderBox
            title={
              <FormattedMessage
                id="expertService.HP.maxTempHW"
                defaultMessage="Maximální teplota teplé vody pro TČ"
              />
            }
            subtitle={
              <FormattedMessage
                id="expertService.HP.maxTempHW.subtitle"
                defaultMessage="Součet flexibilní + nastavené teploty"
              />
            }
            variableId="HP_HW_MAX_SET_TEMP"
            opts={{
              style: 'unit',
              unit: 'celsius',
              maximumFractionDigits: 0,
            }}
            min={40}
            max={70}
          />
        </ParentPaper>
      </Group>
      <Group
        title={<FormattedMessage id="expertService.SP" defaultMessage="Spot" />}
        layout="list"
      >
        <ParentPaper>
          <SliderBox
            title={
              <FormattedMessage
                id="expertService.SP.minDifferenceBatterychargeFromGrid"
                defaultMessage="Minimální odhadované zvýšení SOC pro vyčkání baterie na FVE"
              />
            }
            variableId="SP_MIN_SPOT_DIFFERENCE_BATTERY_SOC"
            subtitle={
              <FormattedMessage
                id="expertService.SP.minDifferenceBatterychargeFromGrid.subtitle"
                defaultMessage="Pokud je odhad nabití baterie pod touto hodnotou, může dojít k nabití baterie ze sítě."
              />
            }
            opts={{ style: 'percent', maximumFractionDigits: 0 }}
            min={5}
            max={70}
          />

          <SliderBox
            title={
              <FormattedMessage
                id="expertService.SP.HwCoefficient"
                defaultMessage="Koeficiet odhadu energie do bojleru"
              />
            }
            variableId="SP_HW_K"
            subtitle={
              <FormattedMessage
                id="expertService.SP.HwCoefficient.subtitle"
                defaultMessage="Koeficient odhadu množství energie pro nabití bojleru."
              />
            }
            opts={{
              maximumFractionDigits: 1,
              minimumFractionDigits: 1,
            }}
            min={0.5}
            max={2.5}
            step={0.1}
          />
          <SliderBox
            title={
              <FormattedMessage
                id="expertService.SP.batteryCoefficient"
                defaultMessage="Koeficiet odhadu času nabíjení baterie"
              />
            }
            variableId="SP_BATTERY_K"
            subtitle={
              <FormattedMessage
                id="expertService.SP.batteryCoefficient.subtitle"
                defaultMessage="Koeficient odhadu času pro nabíjení a vybíjení baterie"
              />
            }
            opts={{
              maximumFractionDigits: 1,
              minimumFractionDigits: 1,
            }}
            min={0.7}
            max={3.0}
            step={0.1}
          />
        </ParentPaper>
      </Group>

      <Group
        title={
          <FormattedMessage id="expertService.WB" defaultMessage="Wallbox" />
        }
        layout="list"
      >
        <ParentPaper>
          <InputBox
            title={
              <FormattedMessage
                id="expertService.WB.IpAdr"
                defaultMessage="IP adresa wallboxu"
              />
            }
            inputVariableId="WB_IP_ADRESS"
            fullWidth
          />
          <SwitchBox
            title={
              <FormattedMessage
                id="expertService.WB.CntComOk"
                defaultMessage="Čítač validních zpráv"
              />
            }
            subtitle={<FormattedValue variableId="WB_cnt_com_ok" />}
            fullWidth
          />
          <SwitchBox
            title={
              <FormattedMessage
                id="expertService.WB.CntComErr"
                defaultMessage="Čítač chybných zpráv"
              />
            }
            subtitle={<FormattedValue variableId="WB_cnt_com_err" />}
            fullWidth
          />
        </ParentPaper>
      </Group>

      <Group
        title={
          <FormattedMessage id="expert_service.network" defaultMessage="Síť" />
        }
        layout="list"
      >
        <Link to={links.exportServiceNetwork}>
          <Button variant="contained">
            <FormattedMessage
              id="expert_service.network.button"
              defaultMessage="Přejít do nastavení sítě"
            />
          </Button>
        </Link>
      </Group>

      <Group
        title={
          <FormattedMessage id="expert_service.xyz" defaultMessage="Servis" />
        }
        layout="list"
      >
        <SimpleTable
          rows={[
            ['Login', ''],
            ...[...serviceLoginList]
              .reverse()
              .filter((row) => row)
              .map((row) => {
                return [row, ''];
              }),
          ]}
          firstLineBold
        />
      </Group>
      <Group
        layout="list"
        title={
          <FormattedMessage
            id="expert_service.stats"
            defaultMessage="Statistiky"
          />
        }
      >
        <StatsDelete />
        <StatsExport />
      </Group>

      <Group
        layout="list"
        title={
          <FormattedMessage
            id="expert_service.app_messages"
            defaultMessage="Zprávy"
          />
        }
      >
        <AppMessagesDelete />
      </Group>

      <Group
        layout="list"
        title={
          <FormattedMessage
            id="expert_service.audit_log"
            defaultMessage="Audit log"
          />
        }
      >
        <AuditLogDelete />
      </Group>

      <Group
        layout="list"
        title={
          <FormattedMessage
            id="expert_service.reports"
            defaultMessage="Reporty"
          />
        }
      >
        <ReportTest />
      </Group>

      <Group
        layout="list"
        title={
          <FormattedMessage
            id="expert_service.error_list"
            defaultMessage="Seznam chyb"
          />
        }
      >
        <SimpleTable
          rows={[
            ['Chyba', ''],
            ...[...errorList].reverse().map((row) => {
              return [row, ''];
            }),
          ]}
          firstLineBold
        />
      </Group>
    </>
  );
};

export default ExpertService;
